import { Link } from "react-router-dom";
import { Youtube } from "lucide-react";
import { getHostClass, urlOnHost } from "@/lib/host";
import { useAuth } from "@/context/AuthContext";
import { DemoCta } from "@/components/demo/DemoCta";
import { analyticsEnabled, openCookieSettings } from "@/lib/analytics";

export default function Footer() {
  const host = getHostClass();
  const isB2B = host === "main";
  const appUrl = urlOnHost("app", "/");
  const affiliateUrl = urlOnHost("app", "/affiliate");
  const { user } = useAuth();
  const isMerchant = user?.role === "merchant";

  return (
    <footer className="mt-32 relative overflow-hidden">
      <div className="blob blob-cyan blob-float w-[400px] h-[400px] -bottom-40 left-10 opacity-30" />
      <div className="blob blob-lav blob-float w-[300px] h-[300px] -bottom-20 right-10 opacity-30" />
      <div className="glass-strong border-t border-white/70 relative">
        <div
          className={`max-w-[1400px] mx-auto px-6 py-16 grid grid-cols-2 gap-8 ${
            isB2B ? "md:grid-cols-5" : "md:grid-cols-6"
          }`}
          data-testid={isB2B ? "footer-b2b" : "footer-app"}
        >
          <div className="col-span-2">
            <img src="/cyannect-logo.png" alt="Cyannect — Marketing Mesh" className="h-16 w-auto mb-4" />
            <p className="text-sm text-slate-600 max-w-xs leading-relaxed">
              {isB2B
                ? "The performance commerce layer for North American brands. Every campaign, tracked. Every dollar, attributed."
                : "The performance commerce layer for North America. Every click, tracked. Every dollar, attributed."}
            </p>
            <div className="flex items-center gap-3 mt-6">
              <a
                href="https://www.youtube.com/@Cyannectinc"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Cyannect on YouTube"
                data-testid="footer-social-youtube"
                className="w-9 h-9 rounded-full pf-btn-glass flex items-center justify-center hover:scale-105 transition-transform"
              >
                <Youtube className="w-4 h-4 text-slate-700" />
              </a>
            </div>
          </div>

          {/* Marketplace column — only rendered on the shopper PWA. B2B site has no shopper surfaces. */}
          {!isB2B && (
            <div data-testid="footer-marketplace-col">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4 font-semibold">Marketplace</div>
              <ul className="space-y-2.5 text-sm text-slate-700">
                <li><Link to="/coupons" className="hover:text-cyan-700">All coupons</Link></li>
                <li><Link to="/stores" className="hover:text-cyan-700">Stores</Link></li>
                <li><Link to="/categories" className="hover:text-cyan-700">Categories</Link></li>
                <li><Link to="/coupons?flash=true" className="hover:text-cyan-700">Flash sales</Link></li>
              </ul>
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4 font-semibold">Merchants</div>
            <ul className="space-y-2.5 text-sm text-slate-700">
              <li><Link to="/register?role=merchant" className="hover:text-cyan-700">Become a partner</Link></li>
              <li><Link to="/merchant" className="hover:text-cyan-700">Merchant portal</Link></li>
              <li><Link to="/pricing" className="hover:text-cyan-700">Pricing</Link></li>
              {isB2B && <li><DemoCta testid="footer-schedule-demo" className="hover:text-cyan-700" /></li>}
              <li><Link to="/merchant/faq" className="hover:text-cyan-700" data-testid="footer-merchant-faq">Merchant FAQ</Link></li>
              {isMerchant && (
                <li><Link to="/developers" className="hover:text-cyan-700" data-testid="footer-api-docs">API docs</Link></li>
              )}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4 font-semibold">Resources</div>
            <ul className="space-y-2.5 text-sm text-slate-700">
              <li><Link to="/faq" className="hover:text-cyan-700" data-testid="footer-faq">FAQ</Link></li>
              <li><Link to="/blog" className="hover:text-cyan-700" data-testid="footer-blog">Blog</Link></li>
              <li><a href={affiliateUrl} className="hover:text-cyan-700" data-testid="footer-affiliate">Affiliate program</a></li>
              {isMerchant && (
                <li><a href="#" className="hover:text-cyan-700" data-testid="footer-analytics">Analytics</a></li>
              )}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4 font-semibold">Company</div>
            <ul className="space-y-2.5 text-sm text-slate-700">
              <li><Link to="/about" className="hover:text-cyan-700" data-testid="footer-about">About</Link></li>
              {/* <li><a href="mailto:careers@cyannect.com" className="hover:text-cyan-700" data-testid="footer-careers">Careers</a></li> */}
              <li><Link to="/privacy" className="hover:text-cyan-700" data-testid="footer-privacy">Privacy</Link></li>
              <li><Link to="/terms" className="hover:text-cyan-700" data-testid="footer-terms">Terms</Link></li>
              {analyticsEnabled() && (
                <li><button type="button" onClick={openCookieSettings} className="hover:text-cyan-700" data-testid="footer-cookie-settings">Cookie settings</button></li>
              )}
              {isB2B && (
                <li>
                  <a href={appUrl} className="hover:text-cyan-700" data-testid="footer-shopper-app">Shopper app →</a>
                </li>
              )}
            </ul>
          </div>
        </div>
        <div className="border-t border-white/60">
          <div className="max-w-[1400px] mx-auto px-6 py-6 text-xs text-slate-500 flex flex-col md:flex-row justify-between gap-2">
            <span>© {new Date().getFullYear()} Cyannect Performance Commerce Inc.</span>
            <span>Canada · United States</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
